"use client"

import Link from "next/link"
import {
  ArrowRight,
  Building2,
  Factory,
  Hammer,
  Layout,
  MoreHorizontal,
  Paintbrush,
  Presentation,
  Store,
} from "lucide-react"
import { motion } from "motion/react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { MagicCard } from "@/components/ui/magic-card"
import VerticalCutReveal from "@/components/ui/fancy/vertical-cut-reveal"

const segments = [
  {
    name: "Menuisier Ébéniste",
    description: "Tradition et innovation",
    icon: Hammer,
    href: "/menuisier-ebeniste",
  },
  {
    name: "Menuisier Agenceur",
    description: "Agencements sur-mesure",
    icon: Layout,
    href: "/menuisier-agenceur",
  },
  {
    name: "Menuisier Standiste",
    description: "Stands d'exposition",
    icon: Presentation,
    href: "/menuisier-standiste",
  },
  {
    name: "Négociant",
    description: "Bois et quincaillerie",
    icon: Store,
    href: "/negociant",
  },
  {
    name: "Collectivité",
    description: "Lycées, CTM, collectivités",
    icon: Building2,
    href: "/collectivites",
  },
  {
    name: "Industriel",
    description: "Performance industrielle",
    icon: Factory,
    href: "/industriel",
  },
  {
    name: "Particulier ou Créateur",
    description: "Passionnés du bois",
    icon: Paintbrush,
    href: "/particuliers-ou-createurs",
  },
  {
    name: "Autres Métiers",
    description: "Paysagistes, imprimeurs",
    icon: MoreHorizontal,
    href: "/autres-metiers",
  },
] as const

export function CustomerSegments() {
  return (
    <section className="section-normal px-4 md:px-6">
      <div className="mx-auto max-w-7xl">
        {/* Section heading */}
        <div className="mb-12">
          <Badge variant="secondary" className="mb-4">
            Nos clients
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl" style={{ textWrap: "balance" }}>
            <VerticalCutReveal
              splitBy="words"
              staggerDuration={0.06}
              staggerFrom="first"
              transition={{ type: "spring", stiffness: 220, damping: 24 }}
            >
              Vous êtes...
            </VerticalCutReveal>
          </h2>
        </div>

        {/* Profession grid */}
        <motion.div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ staggerChildren: 0.06 }}
        >
          {segments.map((segment) => {
            const Icon = segment.icon
            return (
              <motion.div
                key={segment.name}
                variants={{
                  hidden: { opacity: 0, y: 12, scale: 0.97 },
                  visible: { opacity: 1, y: 0, scale: 1 },
                }}
                transition={{ duration: 0.45, ease: [0.25, 0.4, 0, 1] }}
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
              >
              <Link href={segment.href} className="group rounded-xl block transition-[box-shadow,transform] duration-300 ease-[var(--ease-premium)] hover:shadow-[var(--shadow-md)] active:scale-[0.97]">
                <MagicCard className="rounded-xl" gradientFrom="oklch(0.48 0.15 155)" gradientTo="oklch(0.6 0.12 155)" gradientColor="oklch(0.48 0.15 155 / 0.12)">
                  <div className="flex items-start justify-between p-6 md:p-8">
                    <div>
                      <Icon className="size-8 text-primary" />
                      <p className="mt-3 text-sm font-medium">
                        {segment.name}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {segment.description}
                      </p>
                    </div>
                    <ArrowRight className="size-4 text-primary opacity-0 transition-[transform,opacity] duration-200 ease-[var(--ease-out-expo)] group-hover:translate-x-0.5 group-hover:opacity-100" />
                  </div>
                </MagicCard>
              </Link>
              </motion.div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}
